import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermission } from '@/components/rbac/use-permission';
import { P } from '@/lib/rbac/permissions.generated';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/dateFormat';

type Versand = {
  id: number;
  zeitpunkt: string | null;
  kanal: string;
  empfaenger: string;
  betreff: string | null;
  quelle: string;
  ok: boolean;
  fehler: string | null;
  umgeleitet: boolean;
};

type Audit = {
  id: number;
  zeitpunkt: string | null;
  benutzer: string | null;
  berechtigung: string;
  entscheidung: string;
  ressource: string | null;
  scope: string | null;
  grund: string | null;
  ip: string | null;
  /** „sicherheit“ für Anmeldungen, 2FA-Änderungen, Deaktivierungen (ADM-12.1). */
  art?: 'sicherheit' | 'zugriff';
  ereignis?: string | null;
};

const LOGS = P.admin.protokollierung_und_logs;

function useSuchText(wert: string) {
  const [verzoegert, setVerzoegert] = useState(wert);
  useEffect(() => {
    const t = setTimeout(() => setVerzoegert(wert), 300);
    return () => clearTimeout(t);
  }, [wert]);
  return verzoegert;
}

/** Zeitraum für beide Protokolle (ADM-12.1); „bis“ zählt ganz mit. */
function Zeitraum({ von, bis, onVon, onBis }: { von: string; bis: string; onVon: (v: string) => void; onBis: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Input type="date" className="w-40" aria-label="Von" value={von} onChange={(e) => onVon(e.target.value)} />
      <span className="text-sm text-muted-foreground">bis</span>
      <Input type="date" className="w-40" aria-label="Bis" value={bis} onChange={(e) => onBis(e.target.value)} />
    </div>
  );
}

function zeitraumParams(params: URLSearchParams, von: string, bis: string) {
  if (von) params.set('von', von);
  if (bis) params.set('bis', bis);
}

function Versandprotokoll() {
  const [eintraege, setEintraege] = useState<Versand[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [kanal, setKanal] = useState('alle');
  const [nurFehler, setNurFehler] = useState('alle');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [suche, setSuche] = useState('');
  const q = useSuchText(suche);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const params = new URLSearchParams();
      if (kanal !== 'alle') params.set('kanal', kanal);
      if (nurFehler === 'fehler') params.set('nurFehler', 'true');
      if (q.trim()) params.set('q', q.trim());
      zeitraumParams(params, von, bis);
      const antwort = await api.get<{ items: Versand[]; total: number }>(`/api/logs/versand?${params.toString()}`);
      setEintraege(Array.isArray(antwort?.items) ? antwort.items : []);
      setGesamt(Number(antwort?.total || 0));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Versandprotokoll konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, [kanal, nurFehler, q, von, bis]);

  useEffect(() => {
    void laden();
  }, [laden]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        E-Mails und SMS, die aus dem Office verschickt wurden — die letzten 500. Inhalte stehen in der
        Historie des Vorgangs, hier nur Empfänger, Betreff und Ergebnis.
      </p>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <Select value={kanal} onValueChange={setKanal}>
          <SelectTrigger className="w-40" aria-label="Kanal"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Kanäle</SelectItem>
            <SelectItem value="email">E-Mail</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>
        <Select value={nurFehler} onValueChange={setNurFehler}>
          <SelectTrigger className="w-44" aria-label="Ergebnis"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Ergebnisse</SelectItem>
            <SelectItem value="fehler">Nur Fehler</SelectItem>
          </SelectContent>
        </Select>
        <Zeitraum von={von} bis={bis} onVon={setVon} onBis={setBis} />
        <Input className="md:w-72" placeholder="Empfänger, Betreff, Fehler …" value={suche} onChange={(e) => setSuche(e.target.value)} />
        <Button variant="outline" size="icon" onClick={() => void laden()} aria-label="Neu laden">
          <RefreshCw className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground md:ml-auto">{gesamt} Einträge</span>
      </div>
      {laedt ? (
        <LoadingState message="Versandprotokoll wird geladen …" />
      ) : eintraege.length === 0 ? (
        <EmptyState message="Keine Versendungen in dieser Ansicht." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Kanal</TableHead>
                <TableHead>Empfänger</TableHead>
                <TableHead>Betreff</TableHead>
                <TableHead>Quelle</TableHead>
                <TableHead>Ergebnis</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eintraege.map((eintrag) => (
                <TableRow key={eintrag.id}>
                  <TableCell className="whitespace-nowrap">{eintrag.zeitpunkt ? formatDateTime(eintrag.zeitpunkt) : '—'}</TableCell>
                  <TableCell>{eintrag.kanal === 'sms' ? 'SMS' : eintrag.kanal === 'email' ? 'E-Mail' : eintrag.kanal}</TableCell>
                  <TableCell>{eintrag.empfaenger || '—'}</TableCell>
                  <TableCell className="max-w-[18rem] truncate" title={eintrag.betreff || ''}>{eintrag.betreff || '—'}</TableCell>
                  <TableCell>{eintrag.quelle}</TableCell>
                  <TableCell>
                    {eintrag.ok ? (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                        {eintrag.umgeleitet ? 'umgeleitet' : 'versendet'}
                      </Badge>
                    ) : (
                      <div>
                        <Badge variant="outline" className="border-red-300 text-red-700">Fehler</Badge>
                        {eintrag.fehler ? <div className="mt-1 text-xs text-muted-foreground">{eintrag.fehler}</div> : null}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function BerechtigungsAudit() {
  const [eintraege, setEintraege] = useState<Audit[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [entscheidung, setEntscheidung] = useState('alle');
  const [art, setArt] = useState('alle');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [suche, setSuche] = useState('');
  const q = useSuchText(suche);
  const [seite, setSeite] = useState(0);
  const [laedt, setLaedt] = useState(true);
  const proSeite = 100;

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const params = new URLSearchParams({ limit: String(proSeite), offset: String(seite * proSeite) });
      if (entscheidung !== 'alle') params.set('entscheidung', entscheidung);
      if (art !== 'alle') params.set('art', art);
      if (q.trim()) params.set('q', q.trim());
      zeitraumParams(params, von, bis);
      const antwort = await api.get<{ items: Audit[]; total: number }>(`/api/logs/audit?${params.toString()}`);
      setEintraege(Array.isArray(antwort?.items) ? antwort.items : []);
      setGesamt(Number(antwort?.total || 0));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Audit-Log konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, [entscheidung, art, q, seite, von, bis]);

  useEffect(() => {
    void laden();
  }, [laden]);
  useEffect(() => setSeite(0), [entscheidung, art, q, von, bis]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Verweigerte Zugriffe auf sensible Rechte, bedingte und destruktive Aktionen sowie
        Sicherheitsereignisse (Anmeldungen, 2FA-Änderungen, Deaktivierungen) — mit Benutzer, Zeitpunkt und Grund.
      </p>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <Select value={entscheidung} onValueChange={setEntscheidung}>
          <SelectTrigger className="w-44" aria-label="Entscheidung"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Entscheidungen</SelectItem>
            <SelectItem value="deny">Verweigert</SelectItem>
            <SelectItem value="allow">Erlaubt</SelectItem>
          </SelectContent>
        </Select>
        <Select value={art} onValueChange={setArt}>
          <SelectTrigger className="w-48" aria-label="Art"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Arten</SelectItem>
            <SelectItem value="sicherheit">Sicherheitsereignisse</SelectItem>
            <SelectItem value="zugriff">Zugriffe</SelectItem>
          </SelectContent>
        </Select>
        <Zeitraum von={von} bis={bis} onVon={setVon} onBis={setBis} />
        <Input className="md:w-72" placeholder="Recht, Benutzer, Grund …" value={suche} onChange={(e) => setSuche(e.target.value)} />
        <span className="text-sm text-muted-foreground md:ml-auto">{gesamt} Einträge</span>
      </div>
      {laedt ? (
        <LoadingState message="Audit-Log wird geladen …" />
      ) : eintraege.length === 0 ? (
        <EmptyState message="Keine Einträge in dieser Ansicht." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Benutzer</TableHead>
                <TableHead>Recht / Ereignis</TableHead>
                <TableHead>Entscheidung</TableHead>
                <TableHead>Objekt</TableHead>
                <TableHead>Grund</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eintraege.map((eintrag) => (
                <TableRow key={eintrag.id}>
                  <TableCell className="whitespace-nowrap">{eintrag.zeitpunkt ? formatDateTime(eintrag.zeitpunkt) : '—'}</TableCell>
                  <TableCell>{eintrag.benutzer || '—'}</TableCell>
                  <TableCell>
                    {eintrag.art === 'sicherheit' ? (
                      <Badge variant="outline" className="border-sky-300 text-sky-800">{eintrag.ereignis || 'Sicherheit'}</Badge>
                    ) : (
                      <span className="font-mono text-xs">{eintrag.berechtigung}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={eintrag.entscheidung === 'deny' ? 'border-red-300 text-red-700' : 'border-emerald-300 text-emerald-700'}>
                      {eintrag.entscheidung === 'deny' ? 'verweigert' : 'erlaubt'}
                    </Badge>
                    {eintrag.scope ? <div className="mt-1 text-xs text-muted-foreground">{eintrag.scope}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs">{eintrag.ressource || '—'}</TableCell>
                  <TableCell className="max-w-[18rem] text-xs">{eintrag.grund || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {gesamt > proSeite ? (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" disabled={seite === 0} onClick={() => setSeite((s) => s - 1)}>Zurück</Button>
          <span className="text-sm text-muted-foreground">Seite {seite + 1} von {Math.ceil(gesamt / proSeite)}</span>
          <Button variant="outline" size="sm" disabled={(seite + 1) * proSeite >= gesamt} onClick={() => setSeite((s) => s + 1)}>Weiter</Button>
        </div>
      ) : null}
    </div>
  );
}

type Joblauf = {
  job: string;
  name: string;
  zustand: 'ok' | 'fehler' | 'ueberfaellig' | 'noch_nie';
  letzterLauf: string | null;
  letzterStatus: number | null;
  letzterFehler: string | null;
  letzterErfolg: string | null;
  fehler24h: number;
  dauerMs: number | null;
};

const ZUSTAND: Record<Joblauf['zustand'], { label: string; farbe: string }> = {
  ok: { label: 'läuft', farbe: 'border-emerald-300 text-emerald-700' },
  fehler: { label: 'Fehler', farbe: 'border-red-300 text-red-700' },
  ueberfaellig: { label: 'überfällig', farbe: 'border-amber-300 text-amber-800' },
  noch_nie: { label: 'noch nicht gelaufen', farbe: 'border-slate-300 text-slate-500' },
};

/** Hintergrundjobs: letzter Lauf, Zustand, Fehler (T-0121). */
function Hintergrundjobs() {
  const [eintraege, setEintraege] = useState<Joblauf[]>([]);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const antwort = await api.get<{ items: Joblauf[] }>('/api/logs/hintergrundjobs');
      setEintraege(Array.isArray(antwort?.items) ? antwort.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Hintergrundjobs konnten nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const probleme = eintraege.filter((e) => e.zustand === 'fehler' || e.zustand === 'ueberfaellig').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Jeder Lauf wird festgehalten. Scheitert ein Job oder bleibt er aus, geht eine Mail an die
          Alarmadresse — je Job höchstens alle zwölf Stunden.
        </p>
        <Button variant="outline" size="icon" onClick={() => void laden()} aria-label="Neu laden">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      {probleme > 0 ? (
        <p className="text-sm font-medium text-red-700">{probleme} Job(s) brauchen Aufmerksamkeit.</p>
      ) : null}
      {laedt ? (
        <LoadingState message="Hintergrundjobs werden geladen …" />
      ) : eintraege.length === 0 ? (
        <EmptyState message="Noch keine Läufe festgehalten." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Zustand</TableHead>
                <TableHead>Letzter Lauf</TableHead>
                <TableHead>Letzter Erfolg</TableHead>
                <TableHead className="text-right">Fehler (24 h)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eintraege.map((eintrag) => (
                <TableRow key={eintrag.job}>
                  <TableCell>
                    <div className="font-medium">{eintrag.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{eintrag.job}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={ZUSTAND[eintrag.zustand].farbe}>
                      {ZUSTAND[eintrag.zustand].label}
                    </Badge>
                    {eintrag.letzterFehler ? (
                      <div className="mt-1 max-w-[20rem] truncate text-xs text-muted-foreground" title={eintrag.letzterFehler}>
                        HTTP {eintrag.letzterStatus}: {eintrag.letzterFehler}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {eintrag.letzterLauf ? formatDateTime(eintrag.letzterLauf) : '—'}
                    {eintrag.dauerMs !== null ? (
                      <div className="text-xs text-muted-foreground">{(eintrag.dauerMs / 1000).toFixed(1)} s</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {eintrag.letzterErfolg ? formatDateTime(eintrag.letzterErfolg) : '—'}
                  </TableCell>
                  <TableCell className="text-right">{eintrag.fehler24h || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * Protokollierung & Logs (T-0119) mit dem Versandprotokoll (T-0082).
 *
 * Der Menüpunkt zeigte bisher einen leeren Bereich, und das Versandprotokoll wurde
 * geschrieben, aber nirgends angezeigt.
 */
export default function ProtokollePage() {
  const darfVersand = usePermission(LOGS.logs_ansehen);
  const darfAudit = usePermission(LOGS.audit_logs_ansehen);
  const start = darfVersand ? 'jobs' : 'audit';

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Protokollierung &amp; Logs</h1>
        <p className="text-muted-foreground">Ob die Hintergrundjobs laufen, was das System verschickt und wer auf geschützte Bereiche zugegriffen hat.</p>
      </div>
      <Tabs defaultValue={start}>
        <TabsList>
          {darfVersand ? <TabsTrigger value="jobs">Hintergrundjobs</TabsTrigger> : null}
          {darfVersand ? <TabsTrigger value="versand">Versandprotokoll</TabsTrigger> : null}
          {darfAudit ? <TabsTrigger value="audit">Berechtigungen &amp; Sicherheit</TabsTrigger> : null}
        </TabsList>
        {darfVersand ? <TabsContent value="jobs" className="pt-4"><Hintergrundjobs /></TabsContent> : null}
        {darfVersand ? <TabsContent value="versand" className="pt-4"><Versandprotokoll /></TabsContent> : null}
        {darfAudit ? <TabsContent value="audit" className="pt-4"><BerechtigungsAudit /></TabsContent> : null}
      </Tabs>
    </div>
  );
}
